import {
  CONFIG_MAP,
  CONTACT_MAP,
  LEGAL_CONFIG,
  OFFERS_MAP,
  type SiteBrand,
  type SiteContact,
  type SiteLegals
} from '@/Sites'

const DEV_MODE = import.meta.env.VUE_APP_DEV_MODE === 'ON'
const DEV_SUFFIX = '.test'
const COUNTRY_CODE_LENGTH = 2

type ResolvedConfig = SiteBrand &
  SiteContact & {
    legals: SiteLegals
    apiDomain: string
    siteDomain: string
    offersUrl: string
  }

class SiteConfig {
  siteId!: number
  locale!: string
  page!: string
  logo!: string
  phone!: string
  email!: string
  legals!: SiteLegals
  apiDomain!: string
  siteDomain!: string
  offersUrl!: string
  isWiB?: boolean
  theme!: SiteBrand['theme']
  translationPrefix!: string

  constructor() {
    this.init()
  }

  init() {
    const config = _selectConfig()

    this.siteId = config.siteId
    this.locale = config.locale
    this.page = config.page
    this.logo = config.logo
    this.phone = config.phone
    this.email = config.email
    this.legals = config.legals
    this.apiDomain = config.apiDomain
    this.siteDomain = config.siteDomain
    this.offersUrl = config.offersUrl
    this.isWiB = config.isWiB
    this.theme = config.theme
    this.translationPrefix = config.translationPrefix
  }
}

const _selectConfig = (): ResolvedConfig => {
  const hostName = location.hostname
  const domain = _getRef(hostName.substring(hostName.indexOf('.') + 1))
  const siteDomain = hostName.replace(/(^[^.]*.)(\S+)/, 'https://$2')
  const apiDomain = _getApiDomain()
  const config = CONFIG_MAP[domain]
  return {
    ...config,
    apiDomain,
    siteDomain,
    offersUrl: OFFERS_MAP[domain],
    legals: LEGAL_CONFIG[domain],
    ...CONTACT_MAP[domain]
  }
}

const _getApiDomain = (): string => {
  if (DEV_MODE && import.meta.env.VUE_APP_API_DOMAIN) {
    return import.meta.env.VUE_APP_API_DOMAIN
  }
  return location.hostname.replace(/(^[^.]*)(\S+)/, 'https://api$2/v2/crm/')
}

// {domain}-{country}.test --> {domain}.{country}
// Plain string slicing rather than a regex: the domain part can itself contain
// hyphens (wine-in-black-de.test), so matching it needs a greedy group, and that
// backtracks super-linearly on a non-matching host.
const _getRef = (domain: string): string => {
  if (!DEV_MODE || !domain.endsWith(DEV_SUFFIX)) return domain

  const host = domain.slice(0, -DEV_SUFFIX.length)
  const separator = host.length - COUNTRY_CODE_LENGTH - 1

  if (host[separator] !== '-') return domain

  return `${host.slice(0, separator)}.${host.slice(separator + 1)}`
}

export default new SiteConfig()
